import React from "react";
import { motion } from "framer-motion";
import { Building2, MapPin, Hash, ArrowUpRight, Mail, Phone, Layers, Briefcase, UserX } from "lucide-react";

export default function SocieteCard({ societe, onClick }) {
  const formatEuroCompact = (val, currency = "€") => {
    if (val === null || val === undefined) return "—";
    if (val === 0) return `0 ${currency}`;
    const absVal = Math.abs(val);
    const sign = val < 0 ? "-" : "";
    if (absVal >= 1000000) {
      return `${sign}${(absVal / 1000000).toFixed(1)} M${currency}`;
    }
    if (absVal >= 1000) {
      return `${sign}${(absVal / 1000).toFixed(0)} k${currency}`;
    }
    return `${sign}${absVal} ${currency}`;
  };

  const formatSiren = (siren) => {
    if (!siren) return "—";
    const clean = String(siren).replace(/\s/g, "");
    if (clean.length === 9) {
      return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6, 9)}`;
    }
    return siren;
  };

  const legalFormColors = {
    SAS: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    SARL: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
    SCI: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
    SPFPL: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800",
    HOLDING: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
  };

  const formKey = (societe.formeJuridique || "").toUpperCase();
  const badgeStyle = legalFormColors[formKey] || "bg-surface-muted text-primary border-workspace";

  const gerant = societe.gerant;
  const hasGerant = Boolean(gerant && (gerant.nom || gerant.prenom));
  const gerantInitials = hasGerant
    ? `${gerant.prenom?.charAt(0) || ""}${gerant.nom?.charAt(0) || ""}`.toUpperCase() || "D"
    : null;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onClick) onClick();
    }
  };

  return (
    <motion.div
      whileHover={{ y: -2, transition: { duration: 0.15 } }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="
        group relative flex flex-col justify-between h-full
        rounded-[var(--hp-radius-lg)] border border-workspace bg-surface
        shadow-hp-sm hover:border-[var(--hp-color-accent)] hover:shadow-hp-md
        transition-all duration-200 overflow-hidden cursor-pointer
      "
    >
      {/* ========================================================= */}
      {/* 1. PARTIE HAUTE : 2 COLONNES (2/3 IDENTITÉ, 1/3 BADGES)   */}
      {/* ========================================================= */}
      <div className="p-3.5 sm:p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          {/* Colonne Gauche (~2/3) : Logo, Nom, SIREN, Localité, Secteur */}
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-lg bg-secondary/10 text-secondary border border-secondary/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                className="text-sm sm:text-base font-black text-primary truncate group-hover:text-secondary transition-colors"
                title={societe.denomination || societe.raisonSociale || "Société sans nom"}
              >
                {societe.denomination || societe.raisonSociale || "Société sans nom"}
              </h3>
              <p className="text-xs text-subtle flex items-center gap-1.5 mt-0.5 truncate">
                <Hash className="w-3 h-3 shrink-0" />
                <span className="font-mono">{formatSiren(societe.siren)}</span>
                <span>•</span>
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {societe.ville ? `${societe.ville}${societe.codePostal ? ` (${societe.codePostal})` : ""}` : "Ville non renseignée"}
                </span>
              </p>
              {societe.secteurActivite && (
                <p className="mt-1 text-[11px] text-subtle/80 truncate flex items-center gap-1" title={societe.secteurActivite}>
                  <Briefcase className="w-3 h-3 shrink-0 text-subtle/60" />
                  <span className="truncate">{societe.secteurActivite}</span>
                </p>
              )}
            </div>
          </div>

          {/* Colonne Droite (~1/3) : Badges Forme & Statut */}
          <div className="shrink-0 flex flex-col items-end gap-1.5">
            {societe.formeJuridique && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${badgeStyle}`}>
                {societe.formeJuridique}
              </span>
            )}
            {societe.holding && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-secondary/15 text-secondary border border-secondary/20">
                <Layers className="w-2.5 h-2.5" /> Holding
              </span>
            )}
            {societe.statut && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-muted text-primary border border-workspace">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {societe.statut}
              </span>
            )}
          </div>
        </div>

        {/* ========================================================= */}
        {/* 2. ZONE CENTRALE : 3 KPIS FINANCIERS (DISPOSITION FIXE)   */}
        {/* ========================================================= */}
        <div className="grid grid-cols-3 gap-2 pt-0.5">
          <div className="p-1.5 sm:p-2 rounded-lg bg-surface-muted/40 border border-workspace text-center">
            <span className="block text-[10px] font-semibold text-subtle uppercase tracking-wider">CA Annuel</span>
            <span className="text-xs sm:text-sm font-black text-primary mt-0.5 block">
              {formatEuroCompact(societe.chiffreAffaires)}
            </span>
          </div>
          <div className="p-1.5 sm:p-2 rounded-lg bg-surface-muted/40 border border-workspace text-center">
            <span className="block text-[10px] font-semibold text-subtle uppercase tracking-wider">EBITDA</span>
            <span className="text-xs sm:text-sm font-black text-secondary mt-0.5 block">
              {formatEuroCompact(societe.ebitda)}
            </span>
          </div>
          <div className="p-1.5 sm:p-2 rounded-lg bg-surface-muted/40 border border-workspace text-center">
            <span className="block text-[10px] font-semibold text-subtle uppercase tracking-wider">Valorisation</span>
            <span className="text-xs sm:text-sm font-black text-primary mt-0.5 block">
              {formatEuroCompact(societe.valorisationEstimee)}
            </span>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. PARTIE BASSE : GÉRANCE & MANDAT COMPACTÉE              */}
      {/* ========================================================= */}
      <div className="p-3 sm:p-3.5 bg-surface-muted/30 border-t border-workspace space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-[10px] font-semibold text-subtle uppercase tracking-wider flex items-center gap-1.5">
            <Briefcase className="w-3 h-3 text-secondary" />
            Gérance & Mandat
          </span>
          {hasGerant && gerant.detentionCapital != null && (
            <span className="font-bold text-[10px] text-secondary bg-secondary/10 px-1.5 py-0.5 rounded-full border border-secondary/20">
              {gerant.detentionCapital}% Capital
            </span>
          )}
        </div>

        {hasGerant ? (
          <>
            {/* Profil Dirigeant déclaré */}
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-surface border border-workspace shadow-hp-xs flex items-center justify-center font-black text-[11px] text-primary shrink-0">
                {gerantInitials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-primary truncate">
                  {gerant.prenom} {gerant.nom}
                </p>
                <p className="text-[10px] text-subtle truncate">
                  {gerant.role || "Mandataire"}
                  {gerant.statutSocial ? ` • ${gerant.statutSocial}` : ""}
                </p>
              </div>
            </div>

            {/* Coordonnées & CTA */}
            <div className="flex items-center justify-between pt-1 text-[10px] text-subtle border-t border-workspace/60">
              <div className="flex items-center gap-2.5 truncate">
                {gerant.email && (
                  <span className="flex items-center gap-1 truncate" title={gerant.email}>
                    <Mail className="w-2.5 h-2.5 text-secondary shrink-0" />
                    <span className="truncate">{gerant.email}</span>
                  </span>
                )}
                {gerant.telephone && (
                  <span className="flex items-center gap-1 truncate" title={gerant.telephone}>
                    <Phone className="w-2.5 h-2.5 text-secondary shrink-0" />
                    <span className="truncate">{gerant.telephone}</span>
                  </span>
                )}
              </div>

              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-secondary group-hover:translate-x-0.5 transition-transform shrink-0 ml-2">
                Consulter
                <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </>
        ) : (
          /* Bloc Dirigeant non renseigné */
          <div className="flex items-center justify-between pt-0.5">
            <div className="flex items-center gap-1.5 text-subtle text-xs">
              <UserX className="w-3.5 h-3.5 text-subtle/70" />
              <span className="text-[11px]">Dirigeant non renseigné</span>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-secondary group-hover:translate-x-0.5 transition-transform shrink-0">
              Consulter
              <ArrowUpRight className="w-3 h-3" />
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
